import { useEffect, useMemo, useState } from 'react'
import { Check, Eye, Loader2, Minus, Search, X } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useGetUserQuery } from '@/redux/service/userManagement'
import {
  useGetUserWidgetsQuery,
  useSetUserWidgetsMutation,
  type WidgetSummary,
} from '@/redux/service/widgets'
import { WidgetPreviewDialog } from '@/components/widgets/WidgetPreviewDialog'
import { cn } from '@/lib/utils'
import toast from 'react-hot-toast'

type OverrideValue = 'inherit' | 'on' | 'off'

const toOverrideValue = (override: number | null): OverrideValue => {
  if (override === null) return 'inherit'
  return override ? 'on' : 'off'
}

const toIsEnabled = (value: OverrideValue): boolean | null => {
  if (value === 'inherit') return null
  return value === 'on'
}

const OVERRIDE_OPTIONS: {
  value: OverrideValue
  label: string
  icon: typeof Check
}[] = [
  { value: 'off', label: 'Off', icon: X },
  { value: 'inherit', label: 'Inherit', icon: Minus },
  { value: 'on', label: 'On', icon: Check },
]

function OverrideToggle({
  value,
  onChange,
}: {
  value: OverrideValue
  onChange: (value: OverrideValue) => void
}) {
  return (
    <div className='inline-flex shrink-0 rounded-md border border-border bg-muted/40 p-0.5'>
      {OVERRIDE_OPTIONS.map(option => {
        const active = value === option.value
        return (
          <button
            key={option.value}
            type='button'
            onClick={() => onChange(option.value)}
            className={cn(
              'flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors',
              active
                ? option.value === 'on'
                  ? 'bg-emerald-500 text-white'
                  : option.value === 'off'
                    ? 'bg-destructive text-destructive-foreground'
                    : 'bg-secondary text-secondary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <option.icon className='h-3 w-3' />
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

export function UserWidgetOverrides() {
  const [userSearch, setUserSearch] = useState('')
  const { data: usersData } = useGetUserQuery({
    search: userSearch,
    limit: 20,
    status: 'active',
  })
  const users = usersData?.data ?? []

  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [overrides, setOverrides] = useState<Map<number, OverrideValue>>(
    new Map()
  )
  const [previewWidget, setPreviewWidget] = useState<WidgetSummary | null>(null)

  const { data: userWidgetsData, isFetching } = useGetUserWidgetsQuery(
    selectedUserId ?? 0,
    { skip: selectedUserId === null }
  )
  const [setUserWidgets, { isLoading: isSaving }] = useSetUserWidgetsMutation()

  const savedOverrides = useMemo(() => {
    const next = new Map<number, OverrideValue>()
    if (!userWidgetsData) return next
    for (const group of userWidgetsData.data) {
      for (const widget of group.widgets) {
        next.set(widget.id, toOverrideValue(widget.override_enabled))
      }
    }
    return next
  }, [userWidgetsData])

  useEffect(() => {
    setOverrides(savedOverrides)
  }, [savedOverrides])

  const isDirty = Array.from(overrides.entries()).some(
    ([id, value]) => savedOverrides.get(id) !== value
  )

  const handleSave = async () => {
    if (selectedUserId === null) return
    try {
      await setUserWidgets({
        userId: selectedUserId,
        overrides: Array.from(overrides.entries()).map(
          ([widget_id, value]) => ({
            widget_id,
            is_enabled: toIsEnabled(value),
          })
        ),
      }).unwrap()
      toast.success('User widget overrides updated')
    } catch {
      toast.error('Failed to update user widget overrides')
    }
  }

  return (
    <div className='space-y-8'>
      <div className='flex flex-wrap items-center gap-4 rounded-xl border border-border bg-card/50 p-4'>
        <div className='relative w-64'>
          <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground' />
          <Input
            placeholder='Search users...'
            value={userSearch}
            onChange={e => setUserSearch(e.target.value)}
            className='pl-10 h-9 text-xs bg-card'
          />
        </div>
        <Select
          value={selectedUserId !== null ? String(selectedUserId) : ''}
          onValueChange={value => setSelectedUserId(Number(value))}
        >
          <SelectTrigger className='w-64 h-9 text-xs bg-card'>
            <SelectValue placeholder='Select a user' />
          </SelectTrigger>
          <SelectContent>
            {users.map(user => (
              <SelectItem key={user.id} value={String(user.id)}>
                {user.name} ({user.email})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selectedUserId !== null && (
          <div className='ml-auto flex items-center gap-3'>
            {isDirty && (
              <span className='flex items-center gap-1.5 text-xs text-primary'>
                <span className='h-1.5 w-1.5 rounded-full bg-primary' />
                Unsaved changes
              </span>
            )}
            <Button
              onClick={handleSave}
              disabled={isSaving || !isDirty}
              className='h-9 px-4 text-xs'
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        )}
      </div>

      {selectedUserId === null && (
        <p className='text-sm text-muted-foreground'>
          Search and select a user to override their widget access.
        </p>
      )}

      {isFetching && (
        <div className='flex items-center gap-2 text-sm text-muted-foreground'>
          <Loader2 className='h-4 w-4 animate-spin' /> Loading widgets...
        </div>
      )}

      {userWidgetsData?.data.map(group => {
        const overriddenInGroup = group.widgets.filter(
          w => (overrides.get(w.id) ?? 'inherit') !== 'inherit'
        ).length
        return (
          <div key={group.group_id} className='space-y-3'>
            <div className='flex items-center gap-3 border-b border-border pb-2.5'>
              <h3 className='text-xs font-bold uppercase tracking-widest text-foreground'>
                {group.group_name}
              </h3>
              <span className='text-xs text-muted-foreground'>
                ({group.widgets.length})
              </span>
              <span className='ml-auto text-xs text-muted-foreground tabular-nums'>
                {overriddenInGroup} overridden
              </span>
            </div>
            <div className='rounded-lg border border-border bg-card divide-y divide-border overflow-hidden'>
              {group.widgets.map(widget => {
                const value = overrides.get(widget.id) ?? 'inherit'
                return (
                  <div
                    key={widget.id}
                    className='flex items-center gap-4 px-4 py-3.5 transition-colors hover:bg-muted/40'
                  >
                    <div className='min-w-0 flex-1'>
                      <p className='truncate text-sm font-medium text-foreground'>
                        {widget.name}
                      </p>
                      <p className='truncate text-xs text-muted-foreground'>
                        {widget.description}
                      </p>
                    </div>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground'
                      onClick={() => setPreviewWidget(widget)}
                    >
                      <Eye className='h-4 w-4' />
                    </Button>
                    <OverrideToggle
                      value={value}
                      onChange={next =>
                        setOverrides(prev => new Map(prev).set(widget.id, next))
                      }
                    />
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}

      <WidgetPreviewDialog
        widget={previewWidget}
        onClose={() => setPreviewWidget(null)}
      />
    </div>
  )
}
